///
/// EarthBrowser
///
/// Created by Matt Giger on 9/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Rung-1 passive conversion banner for signed-out visitors on their second
/// or third session: a slim, dismissible strip suggesting a free account,
/// leaning on saved-view sync as the concrete benefit. Never blocks
/// interaction; hidden for the day once dismissed. Rungs 0 and 2 never
/// render it (rung 0 shows nothing but the chip, rung 2 escalates to the
/// interstitial).
///

import { Button } from '@/components/ui/button'
import { getBetaStatus } from '@/hooks/useBetaStatus'
import { setAuthOpen } from '@/redux/dialogSlice'
import { bannerDue, conversionRung, dismissBanner } from '@/redux/engagementSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { useGetUserProfileQuery } from '@/redux/userApi'
import { track } from '@/utils/analytics'
import { XIcon } from 'lucide-react'
import { slotActionClass, slotDismissClass, slotPillClass, slotPriority, useNotificationSlot } from './NotificationSlot'
import { useEffect, useRef } from 'react'

export function ConversionBanner() {
	const dispatch = useAppDispatch()
	const { data: userProfile, isLoading } = useGetUserProfileQuery()
	const engagement = useAppSelector(state => state.engagement)
	const { isBeta } = getBetaStatus()

	const rung = conversionRung(engagement)
	const wants = !isBeta && !isLoading && userProfile === undefined && bannerDue(engagement)
	const visible = useNotificationSlot('conversion-banner', wants, slotPriority.conversionBanner)

	// Funnel event: emitted once per appearance, not per re-render
	const capturedShown = useRef(false)
	useEffect(() => {
		if (!visible || capturedShown.current) return
		capturedShown.current = true
		track('conversion_banner_shown', { session_count: engagement.sessionCount, rung })
	}, [visible, engagement.sessionCount, rung])

	if (!visible) return null

	const handleRegister = () => {
		track('conversion_banner_clicked', { rung })
		dispatch(setAuthOpen(true))
	}

	const handleDismiss = () => {
		track('conversion_banner_dismissed', { rung })
		dispatch(dismissBanner())
	}

	return (
		<div className={slotPillClass}>
			<span className="text-xs text-white/90 truncate">
				<span className="sm:hidden">Saved views on every device</span>
				<span className="hidden sm:inline">Keep your saved views on every device</span>
			</span>
			<Button
				size="sm"
				onClick={handleRegister}
				className={`${slotActionClass} bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white transition-all duration-300 hover:scale-105`}
			>
				Create free account
			</Button>
			<button
				onClick={handleDismiss}
				aria-label="Dismiss for today"
				className={slotDismissClass}
			>
				<XIcon className="h-3.5 w-3.5" />
			</button>
		</div>
	)
}
